import { useEffect, useState, type ReactNode } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Eye, MoreHorizontal, Pencil, Trash2 } from "lucide-react";

import { ConfirmDelete } from "@/components/admin/ConfirmDelete";
import { AuditGhostBanner, AuditGhostRecord } from "@/components/admin/AuditGhostRecord";
import { Field } from "@/components/admin/fields";
import { DataTable } from "@/components/data/DataTable";
import { toast } from "@/components/feedback/ToastHost";
import { EmptyState } from "@/components/feedback/EmptyState";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { errorMessage } from "@/lib/errors";
import { isDeletedRecord, useAuditHighlight } from "@/lib/auditHighlight";

export type CrudMode = "create" | "edit";

/** One read-only field rendered in the view dialog. */
export interface DetailField {
  label: string;
  value: ReactNode;
  /** DB column name — enables the audit deep-link yellow border on this field. */
  field?: string;
}

interface FullCrudPageProps<T, TForm> {
  title: string;
  /** The DB table name of the entity (audit deep-links render a ghost copy). */
  entityType?: string;
  columns: ColumnDef<T>[];
  data: T[];
  total: number;
  isLoading: boolean;
  limit: number;
  offset: number;
  onPageChange: (offset: number) => void;
  queryKey: string;
  emptyForm: TForm;
  toForm: (record: T) => TForm;
  renderForm: (form: TForm, setForm: (f: TForm) => void, mode: CrudMode) => ReactNode;
  canSubmit?: (form: TForm, mode: CrudMode) => boolean;
  onSave: (mode: CrudMode, form: TForm, record: T | null) => Promise<unknown>;
  onDelete: (record: T) => Promise<unknown>;
  /**
   * Fetch one record by id. Delete deep-links use it to display the retained
   * (soft-deleted) record in a ghost-styled view dialog; without it — or when
   * the record is truly gone — the audit-replay ghost card is shown instead.
   */
  getRecord?: (id: number) => Promise<T>;
  detailFields: (record: T) => DetailField[];
  recordTitle: (record: T) => string;
  deleteTitle?: (record: T) => string;
  /** Extra content rendered below the detail fields in view mode (e.g. contacts). */
  viewExtras?: (record: T) => ReactNode;
  /** Extra content rendered below the form (record is null on the create form). */
  editExtras?: (record: T | null) => ReactNode;
  /** When provided and falsy for a record, its delete action is hidden. */
  canDelete?: (record: T) => boolean;
}

/** Reusable list + create/view/edit/delete admin page with full-field forms. */
export function FullCrudPage<T extends { id: number }, TForm>({
  title,
  entityType,
  columns,
  data,
  total,
  isLoading,
  limit,
  offset,
  onPageChange,
  queryKey,
  emptyForm,
  toForm,
  renderForm,
  canSubmit,
  onSave,
  onDelete,
  getRecord,
  detailFields,
  recordTitle,
  deleteTitle,
  viewExtras,
  editExtras,
  canDelete,
}: FullCrudPageProps<T, TForm>) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const audit = useAuditHighlight();
  const ghostRecord = isDeletedRecord(audit);
  const [dialog, setDialog] = useState<
    | { mode: "create" }
    | { mode: "view"; record: T; ghost?: boolean }
    | { mode: "edit"; record: T }
    | null
  >(null);
  const [deleting, setDeleting] = useState<T | null>(null);
  const [form, setForm] = useState<TForm>(emptyForm);
  // True when the deleted record cannot be fetched (hard-deleted, or the page
  // has no get endpoint) — the audit-replay ghost card is shown above the list.
  const [ghostFallback, setGhostFallback] = useState(false);

  // Deep-link support: `/admin/x?r=123` opens the record (audit trail links).
  // Deep links for a different entity served by the same page (e.g. `trailer`
  // ids on the sites page) are ignored so no wrong record can open.
  const rParam = searchParams.get("r");
  const [highlight, setHighlight] = useState<string | null>(rParam);
  const entityTypeMatches =
    !audit.entityType || !entityType || audit.entityType === entityType;
  useEffect(() => {
    if (!rParam || isLoading || ghostRecord || !entityTypeMatches) return;
    const record = data.find((item) => String(item.id) === rParam);
    if (record) setDialog({ mode: "view", record });
  }, [rParam, data, isLoading, ghostRecord, entityTypeMatches]);

  // A delete deep-link: the row is soft-deleted but retained in the database,
  // so fetch the real record and display it in a ghost-styled view dialog.
  // Only when it cannot be fetched (hard-deleted, or the page has no get
  // endpoint) fall back to the audit-replay ghost card above the list.
  useEffect(() => {
    if (!ghostRecord) {
      setGhostFallback(false);
      return;
    }
    if (!getRecord || !entityTypeMatches || !audit.recordId) {
      setGhostFallback(true);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const record = await getRecord(Number(audit.recordId));
        if (cancelled) return;
        if (record) setDialog({ mode: "view", record, ghost: true });
        else setGhostFallback(true);
      } catch {
        if (!cancelled) setGhostFallback(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ghostRecord, entityTypeMatches, getRecord, audit.recordId]);

  const refresh = () => void queryClient.invalidateQueries({ queryKey: [queryKey] });

  const save = useMutation({
    mutationFn: async () => {
      if (!dialog) return;
      if (dialog.mode === "edit") {
        return onSave("edit", form, dialog.record);
      }
      return onSave("create", form, null);
    },
    onSuccess: (created) => {
      toast.success(t("admin.saved"));
      refresh();
      // After a create, stay on the same form in edit mode so related records
      // (e.g. contacts) can be added — create and edit feel like one form.
      if (
        dialog?.mode === "create" &&
        created &&
        typeof (created as { id?: unknown }).id === "number"
      ) {
        const record = created as T;
        setForm(toForm(record));
        setDialog({ mode: "edit", record });
      } else {
        setDialog(null);
      }
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const remove = useMutation({
    mutationFn: (record: T) => onDelete(record),
    onSuccess: () => {
      toast.success(t("admin.deleted"));
      setDeleting(null);
      refresh();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const openEdit = (record: T) => {
    setForm(toForm(record));
    setDialog({ mode: "edit", record });
  };

  const openCreate = () => {
    setForm(emptyForm);
    setDialog({ mode: "create" });
  };

  const actionColumn: ColumnDef<T> = {
    id: "actions",
    header: t("common.actions"),
    cell: ({ row }) => (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => setDialog({ mode: "view", record: row.original })}>
            <Eye className="h-4 w-4" /> {t("common.view")}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => openEdit(row.original)}>
            <Pencil className="h-4 w-4" /> {t("common.edit")}
          </DropdownMenuItem>
          {(canDelete ? canDelete(row.original) : true) && (
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={() => setDeleting(row.original)}
            >
              <Trash2 className="h-4 w-4" /> {t("common.delete")}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    ),
  };

  const columnsWithActions = [...columns, actionColumn];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold">{title}</h1>
        </div>
        <Button onClick={openCreate}>{t("admin.create")}</Button>
      </div>

      {!isLoading && total === 0 && !ghostRecord ? (
        <EmptyState title={t("common.emptyState")} />
      ) : (
        <>
          {ghostRecord && ghostFallback ? (
            <AuditGhostRecord
              // The URL's `e` param is authoritative when present — pages that
              // serve several entity types (e.g. sites for site AND trailer).
              entityType={audit.entityType ?? entityType ?? ""}
              entityId={audit.recordId}
            />
          ) : null}
          <DataTable
            columns={columnsWithActions}
            data={data}
            total={total}
            limit={limit}
            offset={offset}
            onPageChange={onPageChange}
            isLoading={isLoading}
            highlightId={highlight}
            onRowDoubleClick={(record) => setDialog({ mode: "view", record })}
          />
        </>
      )}

      <Dialog
        open={dialog !== null}
        onOpenChange={(o) => {
          if (!o) {
            setDialog(null);
            // Leaving the deep-linked record also drops its row highlight.
            setHighlight(null);
          }
        }}
      >
        <DialogContent className="max-w-2xl">
          {dialog !== null ? (
            <>
              <DialogHeader>
                <DialogTitle>
                  {dialog.mode === "create"
                    ? t("admin.create")
                    : dialog.mode === "edit"
                      ? t("admin.edit")
                      : t("admin.view")}
                </DialogTitle>
                {dialog.mode !== "create" ? (
                  <DialogDescription>{recordTitle(dialog.record)}</DialogDescription>
                ) : null}
              </DialogHeader>
              {dialog.mode === "view" ? (
                <div
                  className={`space-y-4${
                    // Ghost styling: the retained (soft-deleted) record.
                    dialog.ghost ? " audit-ghost-record p-4" : ""
                  }`}
                >
                  {dialog.ghost ? <AuditGhostBanner /> : null}
                  <div className="grid grid-cols-2 gap-4">
                    {detailFields(dialog.record).map((item, index) => (
                      <Field key={index} label={item.label} value={item.value} field={item.field} />
                    ))}
                  </div>
                  {viewExtras ? <div>{viewExtras(dialog.record)}</div> : null}
                </div>
              ) : (
                <>
                  {renderForm(form, setForm, dialog.mode)}
                  {editExtras ? (
                    <div className="pt-4">
                      {editExtras(dialog.mode === "edit" ? dialog.record : null)}
                    </div>
                  ) : null}
                  <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" onClick={() => setDialog(null)}>
                      {t("common.cancel")}
                    </Button>
                    <Button
                      onClick={() => save.mutate()}
                      disabled={
                        save.isPending || (canSubmit ? !canSubmit(form, dialog.mode) : false)
                      }
                    >
                      {t("common.save")}
                    </Button>
                  </div>
                </>
              )}
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      <ConfirmDelete
        open={deleting !== null}
        onOpenChange={(o) => !o && setDeleting(null)}
        title={deleteTitle ? deleteTitle(deleting as T) : t("admin.deleteRecord", { name: "" })}
        description={t("admin.deleteRecordDescription")}
        onConfirm={async () => {
          if (deleting) await remove.mutateAsync(deleting);
        }}
      />
    </div>
  );
}
